/**
 * 🚀 LAYOUT ENGINE 2025 - Zentralisierte Layout-Verwaltung
 * 
 * Einheitliches System für alle Widget-Layouts mit:
 * - Zentrale Layout-Patterns
 * - Responsive Breakpoints
 * - Smart Layout-Composition
 * - Optimierte Performance
 */

// =============================================================================
// CORE LAYOUT PATTERNS - Wiederverwendbare Basis-Layouts
// =============================================================================

export const LAYOUT_PATTERNS = {
  // Horizontal Split Patterns
  'horizontal-split': {
    type: 'split',
    direction: 'row',
    mobileDirection: 'column',
    breakpoint: 750,
    distribution: [1, 1], // 50/50 split
    gap: 40,
    mobileGap: 20
  },
  
  'horizontal-split-reverse': {
    type: 'split', 
    direction: 'row-reverse',
    mobileDirection: 'column',
    breakpoint: 750,
    distribution: [1, 1],
    gap: 40,
    mobileGap: 20
  },
  
  // Vertical Stack Patterns
  'vertical-stack': {
    type: 'stack',
    direction: 'column',
    mobileDirection: 'column',
    alignment: 'center',
    gap: 30,
    mobileGap: 20
  },
  
  // Overlay Patterns
  'overlay-centered': {
    type: 'overlay',
    direction: 'column',
    alignment: 'center',
    justifyContent: 'center',
    backgroundMode: true,
    textOverlay: true
  },
  
  // Grid Patterns
  'responsive-grid': {
    type: 'grid',
    columns: { desktop: 3, tablet: 2, mobile: 1 },
    gap: 30,
    mobileGap: 20
  },
  
  // Full Width Patterns
  'full-width': {
    type: 'full',
    direction: 'column',
    width: '100%',
    alignment: 'stretch'
  }
};

// =============================================================================
// RESPONSIVE BREAKPOINTS - Einheitlich für alle Widgets
// =============================================================================

export const BREAKPOINTS = {
  mobile: 750,
  tablet: 1024, 
  desktop: 1400,
  wide: 1920
};

export const getDeviceType = (width) => {
  if (width < BREAKPOINTS.mobile) return 'mobile';
  if (width < BREAKPOINTS.tablet) return 'tablet';
  if (width < BREAKPOINTS.desktop) return 'desktop';
  return 'wide';
};

// =============================================================================
// WIDGET LAYOUT DEFINITIONS - Spezifische Konfigurationen
// =============================================================================

export const WIDGET_LAYOUTS = {
  // HeroHeader Layouts - Vollständige Migration der bestehenden Layouts
  heroHeader: {
    'classic-split': {
      pattern: 'horizontal-split',
      textSection: { flex: 1, alignment: 'left' },
      imageSection: { flex: 1, position: 'right' },
      features: { 
        showHeroImage: true, 
        showLogo: true,
        showDescriptionText: true,
        showTitleText: true,
        showSubtitleText: true,
        showAdditionalText: true,
        showButtonText: true,
        defaultImageWidth: 50,
        mobileStack: true
      }
    },
    
    'text-dominant': {
      pattern: 'horizontal-split',
      textSection: { flex: 2.3, alignment: 'left' },
      imageSection: { flex: 1, position: 'right' },
      features: { 
        showHeroImage: true, 
        showLogo: true,
        showDescriptionText: true,
        showTitleText: true,
        showSubtitleText: true,
        showAdditionalText: true,
        showButtonText: true,
        defaultImageWidth: 30,
        mobileStack: true
      }
    },
    
    
    'full-width-overlay': {
      pattern: 'overlay-centered',
      textSection: { alignment: 'center', overlay: true },
      imageSection: { type: 'background' },
      features: { 
        showBackgroundImage: true, 
        showHeroImage: false,
        showLogo: false,
        showDescriptionText: true,
        showTitleText: true,
        showSubtitleText: true,
        showAdditionalText: true,
        showButtonText: true,
        overlayMode: true,
        mobileStack: false
      }
    },
    
    'centered-content': {
      pattern: 'vertical-stack',
      textSection: { alignment: 'center' },
      imageSection: { position: 'top', size: 'normal' },
      features: { 
        showHeroImage: true, 
        showLogo: true,
        showDescriptionText: true,
        showTitleText: true,
        showSubtitleText: true,
        showAdditionalText: true,
        showButtonText: true,
        mobileStack: false
      }
    },
    
    'centered-minimal': {
      pattern: 'vertical-stack',
      textSection: { alignment: 'center', minimal: true },
      imageSection: { position: 'none' },
      features: { 
        showHeroImage: false,        // Kein Hero Image
        showLogo: false,            // Kein Logo
        showDescriptionText: false, // Oberer Text aus
        showTitleText: true,        // Haupttitel bleibt
        showSubtitleText: false,    // Subtitle aus
        showAdditionalText: true,   // Text über Button bleibt
        showButtonText: true,       // Button bleibt
        minimal: true,              // Minimales Design
        mobileStack: false          // Kein Stacking nötig (nur Text)
      }
    },
    
    'classic-minimal': {
      pattern: 'horizontal-split',
      textSection: { flex: 1, alignment: 'left' },
      imageSection: { flex: 1, position: 'right' },
      features: { 
        showHeroImage: true, 
        showLogo: true,
        showDescriptionText: true,   // Description bleibt (aber nach Title)
        showTitleText: true,         // Haupttitel bleibt
        showSubtitleText: false,     // Subtitle ausgeblendet
        showAdditionalText: false,   // Additional Text ausgeblendet
        showButtonText: true,        // Button bleibt
        hideSubtitle: true,
        hideAdditional: true,
        descriptionAfterTitle: true,
        mobileStack: true
      }
    },
    
    'minimalistic': {
      pattern: 'horizontal-split',
      textSection: { flex: 1, alignment: 'left', compact: true },
      imageSection: { flex: 1, position: 'right' },
      features: { 
        showHeroImage: true, 
        showLogo: true,
        showDescriptionText: true,
        showTitleText: true,
        showSubtitleText: true,
        showAdditionalText: true,
        showButtonText: true,
        compactText: true, 
        roundButton: true,
        defaultImageWidth: 60,
        mobileStack: true
      }
    }
  },
  
  // MethodologyWidget Layouts
  methodologyWidget: {
    'classic-methodology': {
      pattern: 'vertical-stack',
      topSection: { 
        pattern: 'horizontal-split',
        textSection: { flex: 1, alignment: 'left' },
        imageSection: { flex: 1, position: 'right' }
      },
      stepsSection: { 
        pattern: 'responsive-grid',
        columns: { desktop: 3, tablet: 2, mobile: 1 },
        direction: 'horizontal'
      }
    },
    
    'image-top-centered': {
      pattern: 'vertical-stack',
      topSection: {
        pattern: 'vertical-stack',
        imageSection: { size: 'large', alignment: 'center' },
        textSection: { alignment: 'center' }
      },
      stepsSection: {
        pattern: 'responsive-grid', 
        alignment: 'center'
      }
    }
  },
  
  // QAWidget Layouts - NEW LAYOUT SYSTEM 2025
  qaWidget: {
    'classic-side-image': {
      pattern: 'horizontal-split',
      imageSection: { flex: 0.35, position: 'left' },
      contentSection: { flex: 0.65, alignment: 'left' },
      features: {
        showLeftImage: true,
        leftImageWidth: 35,
        leftImageHeight: 400,
        contentSpacing: 40,
        qaItemPadding: 20,
        qaItemSpacing: 0,
        qaItemBorder: true,
        qaItemBorderRadius: 0
      }
    },
    
    
    'full-width-minimal': {
      pattern: 'full-width',
      contentSection: { alignment: 'center' }, // ✅ Zentriert für Symmetrie
      features: { 
        hideImage: true, 
        minimal: true,
        fullWidth: true, // ✅ Volle Breite
        showLeftImage: false,
        contentSpacing: 0,
        qaItemPadding: 24, // ✅ Größeres Padding für Symmetrie
        qaItemSpacing: 0,
        qaItemBorder: true,
        qaItemBorderRadius: 0,
        qaItemBackgroundColor: 'transparent',
        titleAlignment: 'center', // ✅ Zentrierter Titel
        qaAlignment: 'center' // ✅ Zentrierte Q&A Items
      }
    }
  },
  
  // SimplisticQuote Layouts - NEW LAYOUT SYSTEM 2025
  simplicisticQuote: {
    
    'side-image': {
      pattern: 'horizontal-split',
      textSection: { flex: 2, alignment: 'left' },
      imageSection: { flex: 1, position: 'left' },
      features: { 
        showSimpleText: true,
        showRichText: true,
        showContentImage: true,
        showBackgroundImage: false,
        textSpacing: '15px',
        imagePosition: 'left',
        direction: 'row',
        mobileStack: true
      }
    },
    
    'side-image-reverse': {
      pattern: 'horizontal-split-reverse',
      textSection: { flex: 2, alignment: 'left' },
      imageSection: { flex: 1, position: 'right' },
      features: { 
        showSimpleText: true,
        showRichText: true,
        showContentImage: true,
        showBackgroundImage: false,
        textSpacing: '15px',
        imagePosition: 'right',
        direction: 'row',
        mobileStack: true
      }
    },
    
    'image-top-centered': {
      pattern: 'vertical-stack',
      textSection: { alignment: 'center' },
      imageSection: { position: 'top', size: 'large' },
      features: { 
        showSimpleText: true,
        showRichText: true,
        showContentImage: true,
        showBackgroundImage: false,
        showButtonText: true,
        textSpacing: '30px',
        imagePosition: 'top',
        direction: 'column',
        imageFirst: true,
        buttonAlignment: 'center',
        autoHeight: true, // ✅ Auto-Höhe Feature
        mobileStack: false
      }
    }
  },
  
  // SimplisticHeader Layouts - NEW LAYOUT SYSTEM 2025
  simplicisticHeader: {
    'centered-classic': {
      pattern: 'overlay-centered',
      textSection: { alignment: 'center', justifyContent: 'center' },
      iconSection: { position: 'above-title', size: 'medium' },
      features: { 
        showIcon: true,
        showTitleText: true,
        showSubtitleText: true,
        showButtonText: true,
        iconPosition: 'top',
        textAlign: 'center',
        contentDirection: 'column',
        backgroundImageSupport: true,
        centerLayout: true,
        largeTitle: true,
        compactSubtext: true,
        mobileStack: false
      }
    }
  },
  
  // VideoTeaser Layouts - NEW LAYOUT SYSTEM 2025
  videoTeaser: {
    'classic-video-right': {
      pattern: 'horizontal-split',
      textSection: { flex: 1.2, alignment: 'left' },
      videoSection: { flex: 1, position: 'right' },
      features: {
        showRichText: true,
        showDescriptionText: true,
        showButtonText: true,
        showHeaderImage: true,
        showIcon: true,
        showVideo: true,
        videoWidth: 45,
        textWidth: 55,
        mobileStack: true,
        mobileVideoTop: true
      }
    },
    
    'video-left': {
      pattern: 'horizontal-split-reverse',
      textSection: { flex: 1.2, alignment: 'left' },
      videoSection: { flex: 1, position: 'left' },
      features: {
        showRichText: true,
        showDescriptionText: true,
        showButtonText: true,
        showHeaderImage: true,
        showIcon: true,
        showVideo: true,
        videoWidth: 45,
        textWidth: 55,
        mobileStack: true,
        mobileVideoTop: true
      }
    },
    
    'video-dominant': {
      pattern: 'horizontal-split',
      textSection: { flex: 0.7, alignment: 'left' },
      videoSection: { flex: 1.3, position: 'right' },
      features: {
        showRichText: true,
        showDescriptionText: true,
        showButtonText: true,
        showHeaderImage: true,
        showIcon: true,
        showVideo: true,
        videoWidth: 65,
        textWidth: 35,
        mobileStack: true,
        mobileVideoTop: true
      }
    },
    
    'video-top-centered': {
      pattern: 'vertical-stack',
      videoSection: { alignment: 'center', position: 'top' },
      textSection: { alignment: 'center' },
      features: {
        showRichText: true,
        showDescriptionText: true,
        showButtonText: true,
        showHeaderImage: true,
        showIcon: true,
        showVideo: true,
        videoWidth: 80,
        textWidth: 100,
        centerContent: true,
        mobileStack: false
      }
    },
    
    
    'mixed-centered': {
      pattern: 'vertical-custom-order',
      textSection: { alignment: 'center', position: 'top' },
      videoSection: { alignment: 'center', position: 'middle' },
      buttonSection: { alignment: 'center', position: 'bottom' },
      features: {
        showRichText: true,
        showDescriptionText: true,
        showButtonText: true,
        showHeaderImage: false, // ✅ Header Image deaktiviert
        showIcon: true,
        showVideo: true,
        videoWidth: 70,
        textWidth: 100,
        centerContent: true,
        customOrder: true, // ✅ Spezielle Reihenfolge
        mobileStack: false
      }
    }
  },
  
  // SimplisticServices Layouts - NEW LAYOUT SYSTEM 2025
  simplicisticServices: {
    'classic-grid': {
      pattern: 'vertical-stack',
      headerSection: { alignment: 'center' },
      servicesSection: { 
        pattern: 'responsive-grid',
        columns: { desktop: 2, tablet: 2, mobile: 1 },
        gap: 24
      },
      features: {
        showTitleText: true,
        showSubtitleText: true,
        servicesPerRow: 2,
        servicesPerRowMobile: 1,
        servicePadding: 32,
        serviceSpacing: 24,
        serviceBorderRadius: 16,
        serviceShadow: true,
        autoHeight: true
      }
    },
    
    'minimal-cards': {
      pattern: 'vertical-stack',
      headerSection: { alignment: 'center' },
      servicesSection: { 
        pattern: 'responsive-grid',
        columns: { desktop: 2, tablet: 2, mobile: 1 },
        gap: 24
      },
      features: {
        showTitleText: true,
        showSubtitleText: true,
        showServiceIcons: false, // ✅ KEINE ICONS
        servicesPerRow: 2,
        servicesPerRowMobile: 1,
        servicePadding: 32,
        serviceSpacing: 24,
        serviceBorderRadius: 0, // ✅ KANTIGE BORDERS
        serviceShadow: false, // ✅ KEIN SCHATTEN
        serviceBorder: true, // ✅ BORDER AKTIVIERT
        minimal: true,
        autoHeight: true
      }
    },
    
    'luxury-cards': {
      pattern: 'vertical-stack',
      headerSection: { alignment: 'center' },
      servicesSection: { 
        pattern: 'responsive-grid',
        columns: { desktop: 2, tablet: 2, mobile: 1 },
        gap: 32
      },
      features: {
        showTitleText: true,
        showSubtitleText: true,
        servicesPerRow: 2,
        servicesPerRowMobile: 1,
        servicePadding: 40,
        serviceSpacing: 32,
        serviceBorderRadius: 20,
        serviceShadow: true,
        luxury: true,
        autoHeight: true
      }
    },
    
    'clean-image-cards': {
      pattern: 'vertical-stack',
      headerSection: { alignment: 'center' },
      servicesSection: { 
        pattern: 'responsive-grid',
        columns: { desktop: 2, tablet: 2, mobile: 1 },
        gap: 24
      },
      features: {
        showTitleText: true,
        showSubtitleText: true,
        servicesPerRow: 2,
        servicesPerRowMobile: 1,
        servicePadding: 32,
        serviceSpacing: 24,
        serviceBorderRadius: 0, // ✅ KANTIGE CARDS
        serviceShadow: false, // ✅ KEIN SHADOW
        serviceBorder: false, // ✅ KEIN BORDER
        imageMode: true, // ✅ IMAGE-FOKUSSIERT
        serviceImageWidth: 240,
        serviceImageHeight: 330,
        serviceImageWidthMobile: 180,
        serviceImageHeightMobile: 250,
        // ✅ ICON SETTINGS: Kantige Icons mit dickerem Border
        serviceIconBorderWidth: 2,
        serviceIconBorderRadius: 0,
        autoHeight: true
      }
    },
    
    'three-column-clean': {
      pattern: 'vertical-stack',
      headerSection: { alignment: 'center' },
      servicesSection: { 
        pattern: 'responsive-grid',
        columns: { desktop: 3, tablet: 2, mobile: 1 }, // ✅ 3 SPALTEN Desktop
        gap: 24
      },
      features: {
        showTitleText: true,
        showSubtitleText: true,
        servicesPerRow: 3, // ✅ 3 SERVICES PRO REIHE
        servicesPerRowMobile: 1,
        servicePadding: 32,
        serviceSpacing: 24,
        serviceBorderRadius: 0, // ✅ KANTIGE CARDS
        serviceShadow: false, // ✅ KEIN SHADOW
        serviceBorder: false, // ✅ KEIN BORDER
        serviceBackgroundColor: 'BRAND_COLOR_1', // ✅ BRAND COLOR 1 BACKGROUND (wird zur Laufzeit ersetzt)
        imageMode: true, // ✅ IMAGE-FOKUSSIERT wie clean-image-cards
        serviceImageWidth: 240,
        serviceImageHeight: 210, // ✅ NEUE HÖHE: 210px für 3-Spalten-Layout
        serviceImageWidthMobile: 180,
        serviceImageHeightMobile: 160, // ✅ PROPORTIONAL: 160px Mobile
        // ✅ ICON SETTINGS: Kantige Icons mit dickerem Border
        serviceIconBorderWidth: 2,
        serviceIconBorderRadius: 0,
        // ✅ BUTTON SETTINGS: Weiße Buttons mit schwarzer Schrift
        serviceButtonColor: '#ffffff',
        serviceButtonTextColor: '#000000',
        serviceButtonBorderColor: '#000000',
        serviceButtonBorderRadius: 50, // ✅ PERFEKTE RUNDUNG
        serviceButtonBorder: true,
        serviceButtonBorderWidth: 2,
        serviceButtonShadow: false,
        visibleServices: 3, // ✅ NUR 3 SERVICES SICHTBAR
        threeColumn: true, // ✅ MARKER für 3-Spalten-Layout
        clean: true,
        autoHeight: true
      }
    },
    
    'bordered-minimal': {
      pattern: 'vertical-stack',
      headerSection: { alignment: 'center', bordered: true },
      servicesSection: { 
        pattern: 'responsive-grid',
        columns: { desktop: 4, tablet: 2, mobile: 1 },
        gap: 1,
        bordered: true
      },
      features: {
        showTitleText: true,
        showSubtitleText: false, // Kein Subtitle für cleanes Design
        servicesPerRow: 4,
        servicesPerRowMobile: 1,
        servicePadding: 40,
        servicePaddingHorizontal: 25,
        serviceSpacing: 1,
        serviceBorderRadius: 0,
        serviceShadow: false,
        serviceBorder: true,
        serviceBackgroundColor: 'transparent',
        minimal: true,
        bordered: true,
        autoHeight: true,
        minHeight: 500
      }
    },
    
    'bordered-minimal-clean': {
      pattern: 'vertical-stack',
      headerSection: { alignment: 'center', hidden: true }, // ✅ HEADER VERSTECKT
      servicesSection: { 
        pattern: 'responsive-grid',
        columns: { desktop: 4, tablet: 2, mobile: 1 },
        gap: 0, // ✅ KEIN GAP - nahtlose Borders
        bordered: true,
        fullWidth: true // ✅ VOLLE BREITE ohne Container-Padding
      },
      features: {
        showTitleText: false, // ✅ TITEL VERSTECKT
        showSubtitleText: false, // ✅ SUBTITLE VERSTECKT
        showServiceIcons: false, // ✅ KEINE ICONS
        servicesPerRow: 4,
        servicesPerRowMobile: 1,
        servicePadding: 40,
        servicePaddingHorizontal: 25,
        serviceSpacing: 0, // ✅ KEIN GAP - nahtlose Borders (gleich wie bordered-minimal)
        serviceBorderRadius: 0,
        serviceShadow: false,
        serviceBorder: true,
        serviceBackgroundColor: 'transparent',
        containerBorder: false, // ✅ KEINE CONTAINER-BORDERS
        minimal: true,
        bordered: true,
        clean: true, // ✅ CLEAN VERSION
        autoHeight: true,
        minHeight: 0 // ✅ KEINE MINDESTHÖHE
      }
    },
    
    'minimal-four': {
      pattern: 'vertical-stack',
      headerSection: { alignment: 'center' },
      servicesSection: { 
        pattern: 'responsive-grid',
        columns: { desktop: 2, tablet: 2, mobile: 1 },
        gap: 0, // ✅ KEIN GAP - nahtlose Borders
        bordered: true
      },
      features: {
        showTitleText: true,
        showSubtitleText: true,
        showServiceIcons: false, // ✅ KEINE ICONS für clean design
        servicesPerRow: 2, // ✅ 2 SPALTEN statt 4
        servicesPerRowMobile: 1,
        servicePadding: 40,
        servicePaddingHorizontal: 25,
        serviceSpacing: 0, // ✅ KEIN GAP - nahtlose Borders
        serviceBorderRadius: 0,
        serviceShadow: false,
        serviceBorder: true,
        serviceBackgroundColor: 'transparent',
        minimal: true,
        bordered: true,
        autoHeight: true,
        minHeight: 400
      }
    }
  },
  
  // HolisticTestimonial Layouts - NEW LAYOUT SYSTEM 2025
  holisticTestimonial: {
    'classic-grid': {
      pattern: 'responsive-grid',
      headerSection: { alignment: 'center' },
      testimonialsSection: { 
        pattern: 'responsive-grid',
        columns: { desktop: 2, tablet: 2, mobile: 1 },
        gap: 20
      },
      features: {
        showTitleText: true,
        showStars: true,
        showAvatars: true,
        cardsPerRow: 2,
        cardsPerRowMobile: 1,
        cardPadding: 24,
        cardSpacing: 20,
        cardBorderRadius: 12,
        cardShadow: true,
        cardBorder: false,
        autoHeight: true
      }
    },
    
    'linear-list': {
      pattern: 'full-width',
      headerSection: { alignment: 'left' },
      testimonialsSection: { 
        pattern: 'vertical-stack',
        gap: 0,
        fullWidth: true
      },
      features: {
        showTitleText: true,
        showStars: true,
        showAvatars: true,
        cardsPerRow: 1,
        cardsPerRowMobile: 1,
        cardPadding: 32,
        cardSpacing: 0,
        cardBorderRadius: 0,
        cardShadow: false,
        cardBorder: false,
        cardBackgroundColor: 'transparent',
        linearList: true,
        showDividers: true,
        autoHeight: true
      }
    },
    
    'elegant-cards': {
      pattern: 'vertical-stack',
      headerSection: { alignment: 'center' },
      testimonialsSection: { 
        pattern: 'vertical-stack',
        gap: 16,
        alignment: 'center'
      },
      features: {
        showTitleText: true,
        showStars: true,
        showAvatars: true,
        cardsPerRow: 1,
        cardsPerRowMobile: 1,
        cardPadding: 40,
        cardSpacing: 16,
        cardBorderRadius: 16,
        cardShadow: true,
        cardBorder: false,
        cardBackgroundColor: '#ffffff',
        elegant: true,
        autoHeight: true
      }
    }
  },
  
  // SimplisticTestimonial Layouts - NEW LAYOUT SYSTEM 2025
  simplicisticTestimonial: {
    'classic-grid': {
      pattern: 'responsive-grid',
      textSection: { alignment: 'center' },
      features: { 
        testimonialLayout: 'classic-grid',
        gridColumns: { desktop: 2, tablet: 2, mobile: 1 },
        cardStyle: 'elegant',
        showBorder: false,
        showShadow: true
      }
    },
    
    'paper-cards': {
      pattern: 'responsive-grid',
      textSection: { alignment: 'center' },
      features: { 
        testimonialLayout: 'paper-cards',
        gridColumns: { desktop: 2, tablet: 2, mobile: 1 },
        cardStyle: 'vintage',
        showTornEdges: true,
        showShadow: true
      }
    },
    
    'luxury-cards': {
      pattern: 'responsive-grid',
      textSection: { alignment: 'center' },
      features: { 
        testimonialLayout: 'luxury-cards',
        gridColumns: { desktop: 2, tablet: 2, mobile: 1 },
        cardStyle: 'luxury',
        showBorder: true,
        showShadow: true,
        luxuryBackground: true
      }
    },
    
    'full-width-classic': {
      pattern: 'full-width',
      textSection: { alignment: 'left', mobileAlignment: 'center' },
      features: { 
        testimonialLayout: 'full-width-classic',
        stackVertical: true,
        cardStyle: 'clean',
        showBorder: true,
        showShadow: true
      }
    },
    
    'full-width-bordered': {
      pattern: 'full-width',
      textSection: { alignment: 'left', mobileAlignment: 'center' },
      features: { 
        testimonialLayout: 'full-width-bordered',
        stackVertical: true,
        cardStyle: 'bordered',
        showBorder: true,
        showShadow: true
      }
    },
    
    'full-width-fancy': {
      pattern: 'full-width',
      textSection: { alignment: 'left', mobileAlignment: 'center' },
      features: { 
        testimonialLayout: 'full-width-fancy',
        stackVertical: true,
        cardStyle: 'minimal',
        showBorder: false,
        showShadow: false,
        fullHeightImage: true,
        noPadding: true
      }
    }
    
  },
  
  // SpiritualQuoteCard Layouts - NEW LAYOUT SYSTEM 2025
  spiritualQuoteCard: {
    'modern-flat': {
      pattern: 'vertical-stack',
      textSection: { alignment: 'center' },
      features: { 
        showTitleText: true,
        showRichText: true,
        showTopIcon: true,
        showDecorativeSymbol: true,
        showBackgroundImage: false,
        containerStyle: 'flat',
        fullWidth: true,
        straightEdges: true,
        noShadow: true,
        noBorder: true,
        textAlignment: 'center',
        iconPosition: 'top',
        symbolPosition: 'between'
      }
    },
    
    'elegant-minimal': {
      pattern: 'vertical-stack',
      textSection: { alignment: 'center' },
      features: { 
        showTitleText: true,
        showRichText: true,
        showTopIcon: true,
        showDecorativeSymbol: true,
        showBackgroundImage: false,
        containerStyle: 'elegant',
        elegantSpacing: true,
        subtleShadow: true,
        softBorder: true,
        blurEffect: true,
        textAlignment: 'center',
        iconPosition: 'top',
        symbolPosition: 'between',
        premiumPadding: true
      }
    },
    
    'luxury-minimal': {
      pattern: 'vertical-stack',
      textSection: { alignment: 'center' },
      features: { 
        showTitleText: true,
        showRichText: true,
        showTopIcon: true,
        showDecorativeSymbol: true,
        showBackgroundImage: false,
        containerStyle: 'luxury',
        luxurySpacing: true,
        premiumShadow: true,
        refinedBorder: true,
        advancedBlur: true,
        insetHighlight: true,
        textAlignment: 'center',
        iconPosition: 'top',
        symbolPosition: 'between',
        luxuryPadding: true,
        largerIcons: true
      }
    }
  }
};

// =============================================================================
// LAYOUT ENGINE - Zentrale Layout-Generierung
// =============================================================================

export class LayoutEngine {
  
  /**
   * Generiert Layout-Styles für ein Widget
   */
  static generateLayout(widgetType, layoutId, data, deviceType = 'desktop') {
    const layoutConfig = WIDGET_LAYOUTS[widgetType]?.[layoutId];
    if (!layoutConfig) {
      console.warn(`Layout ${layoutId} for ${widgetType} not found`);
      return this.getDefaultLayout();
    }
    
    const pattern = LAYOUT_PATTERNS[layoutConfig.pattern];
    if (!pattern) {
      console.warn(`Pattern ${layoutConfig.pattern} not found`);
      return this.getDefaultLayout();
    }
    
    return this.composeLayout(pattern, layoutConfig, data, deviceType);
  }
  
  /**
   * Komponiert Layout aus Pattern und Config
   */
  static composeLayout(pattern, config, data, deviceType) {
    const isMobile = deviceType === 'mobile';
    
    const layout = {
      // Container Styles
      container: {
        display: 'flex',
        flexDirection: isMobile ? (pattern.mobileDirection || pattern.direction) : pattern.direction,
        gap: `${isMobile ? pattern.mobileGap : pattern.gap}px`,
        width: '100%',
        ...(pattern.alignment && { alignItems: pattern.alignment }),
        ...(pattern.justifyContent && { justifyContent: pattern.justifyContent })
      },
      
      // Section Styles
      sections: this.generateSectionStyles(config, pattern, isMobile),
      
      // Responsive Properties
      responsive: {
        deviceType,
        isMobile,
        breakpoint: pattern.breakpoint || BREAKPOINTS.mobile
      },
      
      // Features
      features: config.features || {}
    };
    
    return layout;
  }
  
  /**
   * Generiert Section-spezifische Styles
   */
  static generateSectionStyles(config, pattern, isMobile) {
    const sections = {};
    
    // Text Section
    if (config.textSection) {
      sections.text = {
        flex: config.textSection.flex || 1,
        textAlign: config.textSection.alignment || 'left',
        ...(isMobile && { textAlign: 'center' }), // Mobile override
        ...(config.textSection.compact && { fontSize: '0.9em' })
      };
    }
    
    // Image Section  
    if (config.imageSection) {
      sections.image = {
        flex: config.imageSection.flex || 1,
        position: config.imageSection.position || 'right',
        ...(config.imageSection.type === 'background' && { 
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: -1
        })
      };
    }
    
    // Content Section (für QA, etc.)
    if (config.contentSection) {
      sections.content = {
        flex: config.contentSection.flex || 1,
        textAlign: config.contentSection.alignment || 'left'
      };
    }
    
    return sections;
  }
  
  /**
   * Responsive Werte berechnen
   */
  static getResponsiveValue(data, property, deviceType, fallback = 'auto') {
    const deviceProperty = `${property}${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}`;
    return data[deviceProperty] || data[property] || fallback;
  }
  
  /**
   * Default Layout fallback
   */
  static getDefaultLayout() {
    return {
      container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        width: '100%'
      },
      sections: {},
      responsive: { deviceType: 'desktop', isMobile: false },
      features: {}
    };
  }
  
  /**
   * Layout validieren
   */
  static validateLayout(widgetType, layoutId) {
    return !!(WIDGET_LAYOUTS[widgetType]?.[layoutId]);
  }
  
  /**
   * Alle verfügbaren Layouts für Widget-Type
   */
  static getAvailableLayouts(widgetType) {
    return Object.keys(WIDGET_LAYOUTS[widgetType] || {});
  }
}

// =============================================================================
// RESPONSIVE UTILITIES
// =============================================================================

export const ResponsiveUtils = {
  
  /**
   * CSS Custom Properties für Responsive Design
   */
  generateCSSProps(data, properties) {
    const cssProps = {};
    
    properties.forEach(prop => {
      cssProps[`--${prop}-mobile`] = this.appendUnit(data[`${prop}Mobile`] || data[prop]);
      cssProps[`--${prop}-tablet`] = this.appendUnit(data[`${prop}Tablet`] || data[prop]);
      cssProps[`--${prop}-desktop`] = this.appendUnit(data[prop]);
    });
    
    return cssProps;
  },
  
  /**
   * Unit Helper
   */
  appendUnit(value, unit = 'px') {
    if (!value || value === 'auto' || value === 'none') return value;
    if (typeof value === 'string' && isNaN(value)) return value;
    return `${parseInt(value)}${unit}`;
  },
  
  /**
   * Breakpoint Media Queries
   */
  getMediaQuery(breakpoint) {
    const bp = BREAKPOINTS[breakpoint];
    return bp ? `@media (max-width: ${bp}px)` : '';
  }
};

export default LayoutEngine;
